import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../../services/apiClientService';
import { Routes } from '../../../routes/RouteConstants';
import useErrorHandler, { isBookingTooLate } from '../../../hooks/useErrorHandler';
import { useTenant } from '../../../contexts/TenantContext';

const isSlotTaken = (error) => error?.response?.data?.code === "DEC-0001";

/**
 * `preReserve` resolves to the held booking, or nothing when there is none: its `providerId` is the
 * provider who serves it, the one the backend picked for „Bárki” (docs/MULTI_PROVIDER.md).
 *
 * `onSlotLost` runs when the slot can no longer be had, for the page that shows the list the guest
 * picked it from: that list is out of date.
 */
const usePreReserve = ({ onSlotLost } = {}) => {
  const { tenantId } = useTenant();
  const [loading, setLoading] = useState(false);
  const { handleError } = useErrorHandler();
  const navigate = useNavigate();

  const preReserve = async (requestData) => {
    setLoading(true);

    try {
      const url = `/v1/tenants/${tenantId}/bookings/pre-reserve`
      const response = await apiClient.post(url, requestData);
      return response.data;
    } catch (error) {
      // After sign-in the guest lands on the confirmation, which pre-reserves as it opens.
      handleError(error, Routes.CONFIRM_BOOKING);
      // Someone else was faster, or the slot came within the tenant's booking deadline
      // (DECISIONS.md D30): the guest has to pick again, and the snackbar says why.
      if (isSlotTaken(error) || isBookingTooLate(error)) {
        navigate(Routes.BOOKING, { replace: true });
        onSlotLost?.();
      }
    } finally {
      setLoading(false);
    }
  };

  return { loading, preReserve };
};

export default usePreReserve;
